import React, { useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { Paper } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { HeaderMarginContainer } from 'Deer/components/HeaderComponents/HeaderMarginContainer';
import { getApplicableL2MyDataModules } from 'Deer/components/navigation/secondary/ModuleSection';
import { L3_PATHS } from 'Deer/constants/routes';
import { DeerBreadcrumbs } from 'Deer/intent/components/Layout/Layout';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useQuery as useQueryParams } from 'components/hooks/useQuery';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  clearMLSummaryFeedback,
  REVIEW_FORM_KEYS,
  setIsSelectedFAQSection,
  setSelectedReviewPeriodSummarySection,
  useIsSelectedFAQSection,
  useSelectedReviewForm,
  useSelectedReviewPeriodSummarySection,
} from 'reducers/reviewFormSlice';
import {
  checkSimplifiedReviewCreationEnabled,
  checkIntentLandingPagesFeatureEnabled,
  checkNewNavEnabled,
} from 'utils/helpers/featureFlags';

import { ArrowBack } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/noFeedbacks.svg';

import { REVIEW_FORM_COMPLETION_STATUS } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getReviewResultRoute, findAndReplaceString } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { isNetchexLogin } from 'constants/netChexConstants';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { BreadCrumb, BreadcrumbItem, BreadcrumbLink } from 'uiToolkitV1/BreadCrumb';
import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextHeader2 } from 'uiToolkitV1/Typography';

import { REVIEW_TABLE_TYPES } from './LandingPage/ReviewAudit';
import CongratsFooter from './components/CongratsFooter/CongratsFooter';
import { PerformanceResultScore } from './components/CongratsFooter/components/PerformanceScore';
import FeedbackReviewPeriodSummary from './components/FeedbackReviewPeriodSummary/FeedbackReviewPeriodSummary';
import { useInvalidateManagerSummary } from './components/ManagerAISummary/hooks/dataHooks';
// eslint-disable-next-line import/no-cycle
import ReviewPeriodSummaryGoals from './components/ReviewPeriodSummary/ReviewPeriodSummaryGoals';
import { ReviewPeriodSummaryInitiatives } from './components/ReviewPeriodSummaryInitiatives/ReviewPeriodSummaryInitiatives';
import {
  ReviewFormPreviewHeaderNote,
  SwitchableReviewFormPreviewHeaderNote,
} from './components/ReviewPreviewHeaderNote';
import REVIEW_FORM_TYPES from './components/Sidebar/common/REVIEW_FORM_TYPES';
import { useReviewFAQData, useReviewFormSidebarDataOld } from './components/Sidebar/hooks/dataHooks';
import { OverallFormScore } from './components/newFormComponents/OverallFormScore';
import { useReviewPreviewParams } from './hooks/reviewPreviewHooks';
import useReviewFormParams from './hooks/useReviewFormParams';

const StyledPaper = styled(Box)`
  background: ${(props) => props.theme.colors.neutral.F5F7F9};
  border-radius: 0;
  margin-bottom: 16px;
`;

const FormContainer = styled.div`
  padding: ${() => (checkNewNavEnabled() ? '0px' : '12px')};
  padding-top: 0px;
  margin-top: 12px;
  flex-grow: 1;
  border-radius: 4px;
  box-shadow: none;
`;

export const REVIEW_SUMMARY_SECTIONS = {
  GOALS: {
    id: 'GOALS',
    reviewSummaryTitle: 'Goals',
    component: ReviewPeriodSummaryGoals,
  },

  INITIATIVES: {
    id: 'INITIATIVES',
    reviewSummaryTitle: 'Initiatives',
    component: ReviewPeriodSummaryInitiatives,
  },

  FEEDBACK: {
    id: 'FEEDBACK',
    reviewSummaryTitle: 'Feedback',
    component: FeedbackReviewPeriodSummary,
  },
};

const myReviewsPendingPath = `${L3_PATHS['my-data'].review.myReviews}?activeTableId=${REVIEW_TABLE_TYPES.PENDING_ON_ME}`;

const HeaderBreadCrumb = () => {
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const queryClient = useQueryClient();
  const sidebarData = queryClient.getQueryData([REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId]);
  const { employee } = sidebarData ?? {};

  return (
    <BreadCrumb>
      <BreadcrumbItem>
        <BreadcrumbLink
          to={checkNewNavEnabled() ? L3_PATHS['my-data'].review.myReviews : routes.performanceReview.index}
        >
          My Reviews
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbItem>
        <BreadcrumbLink
          to={`${
            checkNewNavEnabled() ? L3_PATHS['my-data'].review.myReviews : routes.performanceReview.audit
          }?activeTableId=${REVIEW_TABLE_TYPES.PENDING_ON_ME}`}
        >
          Pending On Me
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbItem isCurrentPage>
        <BreadcrumbLink>{employee?.displayName}</BreadcrumbLink>
      </BreadcrumbItem>
    </BreadCrumb>
  );
};

const ReviewSummaryHeaderBreadCrumb = () => (
  <BreadCrumb>
    <BreadcrumbItem>
      <BreadcrumbLink>Review Process</BreadcrumbLink>
    </BreadcrumbItem>

    <BreadcrumbItem isCurrentPage>
      <BreadcrumbLink>Review Context</BreadcrumbLink>
    </BreadcrumbItem>
  </BreadCrumb>
);

const ReviewFAQHeaderBreadCrumb = () => {
  const selectedReviewForm = useSelectedReviewForm();
  return (
    <BreadCrumb>
      <BreadcrumbItem>
        <BreadcrumbLink>{selectedReviewForm}</BreadcrumbLink>
      </BreadcrumbItem>

      <BreadcrumbItem isCurrentPage>
        <BreadcrumbLink>Review FAQs</BreadcrumbLink>
      </BreadcrumbItem>
    </BreadCrumb>
  );
};

const Header = () => {
  const selectedReviewForm = useSelectedReviewForm();
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const { moduleName } = useOrganisationSettings();
  const { isPreview } = useReviewPreviewParams();
  const queryParams = useQueryParams();

  const queryClient = useQueryClient();
  const sidebarData = queryClient.getQueryData([REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId]);
  const { employee } = sidebarData ?? {};

  const applicableL2MyDataModules = getApplicableL2MyDataModules(moduleName) ?? [];

  const history = useHistory();

  const { data: formData = {} } = useReviewFormSidebarDataOld({ employeeId, providerId, reviewId });
  const { isShowGoalRating, isShowCompetencyRating, isShowQNARating } = formData;

  let showScore = false;
  if (isShowGoalRating && selectedReviewForm === REVIEW_FORM_KEYS.GOAL) {
    showScore = true;
  } else if (isShowCompetencyRating && selectedReviewForm === REVIEW_FORM_KEYS.COMPETENCY) {
    showScore = true;
  } else if (isShowQNARating && selectedReviewForm === REVIEW_FORM_KEYS.QNA) {
    showScore = true;
  }
  const { displayName } = REVIEW_FORM_TYPES[selectedReviewForm] ?? {};

  /**
   * Handles back navigation
   * - Goes to previous page if history exists
   * - Otherwise redirects based on source query param:
   *   - Me/myData -> Me > Reviews > Pending on me
   *   - team -> Skip Team > Reviews > Completion status
   *   - company -> Company > Reviews > Completion status
   * - Defaults to Me > Reviews > Pending on me
   */
  const goBack = () => {
    const { source } = queryParams;
    if (history.length > 2) {
      history.goBack();
      return;
    }

    const teamAllReportsCompletionPath = L3_PATHS.team.allReports.review.completionStatus;
    const companyCompletionPath = L3_PATHS.company.review.completionStatus;

    if (source === 'Me' || source === 'myData') {
      history.push(myReviewsPendingPath);
    } else if (source === 'team') {
      history.push(teamAllReportsCompletionPath);
    } else if (source === 'company') {
      history.push(companyCompletionPath);
    } else {
      history.push(myReviewsPendingPath);
    }
  };

  return (
    <HeaderMarginContainer
      wrapperProps={
        isNetchexLogin && !isMobileView()
          ? {
              bg: 'white',
              m: '8px 0 0 0',
              px: '12px',
              minH: '64px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.24)',
            }
          : {}
      }
    >
      <Box
        pr="12px"
        w="100%"
        pt={checkIntentLandingPagesFeatureEnabled() ? '0' : '12px'}
        pl={checkIntentLandingPagesFeatureEnabled() ? '0' : '12px'}
        p={checkNewNavEnabled() ? 0 : 3}
        bg={checkNewNavEnabled() ? 'transparent' : 'neutral.F5F7F9'}
      >
        <Flex flexDir="column" alignItems="flex-start" gap={3}>
          {!isNetchexLogin && (
            <Flex
              alignItems="center"
              sx={{
                '.back-button': {
                  paddingLeft: '0px',
                },
              }}
            >
              {checkIntentLandingPagesFeatureEnabled() && !isPreview && (
                <DeerBreadcrumbs
                  breadcrumbs={{
                    items: [
                      {
                        label: 'Me',

                        to: applicableL2MyDataModules[0]?.value,
                      },
                      {
                        label: 'Reviews',
                        to: L3_PATHS['my-data'].review.myReviews,
                      },
                    ],
                    page: {
                      label: `Review form for ${employee?.displayName}`,
                      to: '',
                    },
                  }}
                />
              )}
              {!checkIntentLandingPagesFeatureEnabled() && (
                <>
                  {!isPreview && (
                    <Button
                      mr="4px"
                      StartIcon={ArrowBack}
                      variant="tertiary"
                      iconSize="24px"
                      onClick={goBack}
                      className="back-button"
                    />
                  )}
                  {!isPreview && <HeaderBreadCrumb />}
                </>
              )}
            </Flex>
          )}
          <PerformanceResultScore margin="0" heading={`Calibrated Ratings for ${employee?.displayName}`} />
          <Flex justifyContent="space-between" alignItems="center" width="100%" ml={isMobileView() ? '20px' : '0'}>
            <Flex mt="4px" alignItems="center" gap="12px" mb={2}>
              {isNetchexLogin && (
                <Button
                  StartIcon={ArrowBack}
                  variant="tertiary"
                  onClick={goBack}
                  startIconProps={{ color: '#666666 !important' }}
                  style={{ padding: '0px' }}
                />
              )}
              <TextHeader2 bold color="text.g1">
                {findAndReplaceString(
                  displayName === 'Goals Review' ? moduleName : displayName,
                  modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE),
                  'Goals'
                )}
                s
              </TextHeader2>
            </Flex>

            {!isMobileView() && <Flex alignItems="center">{showScore && <OverallFormScore />}</Flex>}
          </Flex>
        </Flex>
      </Box>
    </HeaderMarginContainer>
  );
};

const ReviewSummaryHeader = () => {
  const selectedReviewPeriodSummarySection = useSelectedReviewPeriodSummarySection();
  const dispatch = useDispatch();
  const { moduleName } = useOrganisationSettings();

  const backClickHandler = () => {
    dispatch(setSelectedReviewPeriodSummarySection({ id: null }));
  };

  return (
    <StyledPaper p="14px 12px" pl={checkNewNavEnabled() ? 0 : '14px'}>
      <Flex justifyContent="flex-start" alignItems="center">
        <Button StartIcon={ArrowBack} variant="tertiary" iconSize="32px" onClick={backClickHandler} />

        <Box ml={3}>
          <ReviewSummaryHeaderBreadCrumb />
          <Flex mt="4px">
            <TextHeader2 bold color="text.g1">
              {findAndReplaceString(
                REVIEW_SUMMARY_SECTIONS[selectedReviewPeriodSummarySection].reviewSummaryTitle === 'Goals'
                  ? modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)
                  : REVIEW_SUMMARY_SECTIONS[selectedReviewPeriodSummarySection].reviewSummaryTitle
              )}{' '}
              Review Period Summary
            </TextHeader2>
          </Flex>
        </Box>
      </Flex>
    </StyledPaper>
  );
};

const ReviewFAQHeader = () => {
  const dispatch = useDispatch();

  const backClickHandler = () => {
    dispatch(setIsSelectedFAQSection(false));
  };

  return (
    <StyledPaper p="14px 12px">
      <Flex justifyContent="flex-start" alignItems="center">
        <Button StartIcon={ArrowBack} variant="tertiary" iconSize="32px" onClick={backClickHandler} />
        <Box ml={3}>
          {/* <ReviewFAQHeaderBreadCrumb /> */}
          <Flex mt="4px">
            <TextHeader2 bold color="text.g1">
              Review FAQs
            </TextHeader2>
          </Flex>
        </Box>
      </Flex>
    </StyledPaper>
  );
};

const ReviewPeriodSummarySection = (statProps) => {
  const selectedReviewPeriodSummarySection = useSelectedReviewPeriodSummarySection();

  const Comp = REVIEW_SUMMARY_SECTIONS?.[selectedReviewPeriodSummarySection]?.component;

  return <Comp {...statProps} />;
};

const ReviewFAQSection = () => {
  const { reviewId } = useReviewFormParams();
  const { data, isLoading } = useReviewFAQData({ reviewId });
  if (isLoading) {
    return <SkeletonLoader />;
  }
  if (!data.length) {
    return (
      <Paper p={3}>
        <EmptyScreen
          heading="Nothing to see here!"
          description="No FAQs have been created by the admin yet"
          illustrationSrc={NoDataImg}
          sx={{ height: '100%' }}
        />
      </Paper>
    );
  }
  return null;
};

const FormsPageRabbit = () => {
  const selectedReviewForm = useSelectedReviewForm();
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const toast = useToast();
  const dispatch = useDispatch();
  const history = useHistory();
  const managerSummaryCacheInvalidator = useInvalidateManagerSummary();
  const { isPreview } = useReviewPreviewParams();

  const {
    data: formData = {},
    isError,
    error,
  } = useReviewFormSidebarDataOld({
    employeeId,
    providerId,
    reviewId,
  });

  const { reviewForms, isReviewStarted, isDueDatePassed } = formData ?? {};
  const reviewFormSize = reviewForms?.length ?? 0;
  const lastFormName = Array.isArray(reviewForms) ? reviewForms[reviewFormSize - 1]?.formName : '';
  const userIsOnLastForm = lastFormName === selectedReviewForm;

  const selectedReviewPeriodSummarySection = useSelectedReviewPeriodSummarySection();
  const isSelectedFAQSection = useIsSelectedFAQSection();
  const FormComponent = REVIEW_FORM_TYPES[selectedReviewForm]?.component;

  if (!isPreview) {
    if (isDueDatePassed === true) {
      toast({
        status: 'error',
        title: 'The due date for filling this form has passed. Please reach out to your mesh admin for any queries.',
      });
      history.push(routes.performanceReview.audit);
    } else if (isReviewStarted === false) {
      toast({ status: 'error', title: 'The review process has yet to commence.' });
      history.push(routes.performanceReview.audit);
    }
  }

  if (isError) {
    console.error('Form Header call failed');
    history.replace(myReviewsPendingPath);
  }

  if (
    [REVIEW_FORM_COMPLETION_STATUS.ACKNOWLEDGED, REVIEW_FORM_COMPLETION_STATUS.SUBMITTED].includes(
      formData?.reviewInfo?.submissionStatus
    )
  ) {
    history.replace(
      getReviewResultRoute(formData?.reviewInfo?.feedbackForm?.id, formData?.reviewInfo?.receiverEmployeeId)
    );
  }

  // using useEffect to run only once
  useEffect(
    () => () => {
      dispatch(clearMLSummaryFeedback());
      managerSummaryCacheInvalidator();
      if (isError) {
        toast({ title: error?.message || 'Some issue occurred!', status: 'error' });
      }
    },
    [dispatch, error?.message, isError, managerSummaryCacheInvalidator, toast]
  );

  if (!FormComponent) {
    // <- if FormComponent is not a jsx or is falsy value
    return null;
  }

  if (isSelectedFAQSection) {
    return (
      <>
        <ReviewFAQHeader />
        <FormContainer>
          <ReviewFAQSection />
        </FormContainer>
      </>
    );
  }

  if (selectedReviewPeriodSummarySection) {
    return (
      <>
        <ReviewSummaryHeader />
        <FormContainer>
          <ReviewPeriodSummarySection {...formData} />
        </FormContainer>
      </>
    );
  }

  return (
    <Flex h={`calc(100vh - ${checkNewNavEnabled() ? '0' : '52px'})`} flexDir="column" overFlowY="scroll">
      {!checkSimplifiedReviewCreationEnabled() && <ReviewFormPreviewHeaderNote />}
      {checkSimplifiedReviewCreationEnabled() && <SwitchableReviewFormPreviewHeaderNote />}
      <Header />
      <FormContainer>
        <FormComponent />
        {userIsOnLastForm && <CongratsFooter />}
      </FormContainer>
    </Flex>
  );
};

export default FormsPageRabbit;
